import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import type { UploadFile } from 'antd'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  ImageUploadWithPreview,
  Input,
  InputNumber,
  Select,
  Spin,
  Tooltip,
  message,
} from '@/components/ui'
import { DeleteOutlined, FilePdfOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons'
import { api } from '@/lib/apiClient'
import { keys } from '@/lib/queryKeys'
import {
  CERT_DESIGN_DPI,
  CERT_FONT_FAMILIES,
  CERT_MAX_FONT_SIZE_PX,
  CERT_MIN_FONT_SIZE_PX,
  CERT_PRESETS,
  MAX_CERT_DIMENSION_IN,
  MIN_CERT_DIMENSION_IN,
  certificateCanvasSizeInPixels,
  certificateFontCss,
  certificateFontWeightOptions,
  coerceCertificateFontWeight,
  isSingleWeightCertificateFont,
  buildCertificatePreviewData,
  createCertificateElementId,
  createDefaultCertificateTemplate,
  createDefaultCertificateTextStyle,
  ensureCertificatePresetConsistency,
  normalizeCertificateTemplate,
  resolveCertificateElementText,
  withSequentialZIndex,
} from '@/lib/certificateTemplate'
import type {
  CertificateElement,
  CertificateEventData,
  CertificateMergeTag,
  CertificatePreset,
  CertificatePreviewRegistrant,
  CertificateRegistrantSearchResponse,
  CertificateTemplate,
  CertificateTemplateResponse,
  CertificateTextAlign,
  CertificateTextTransform,
} from '@/types/certificateDesigner'

/** Matches the server's MAX_BACKGROUND_BYTES / ALLOWED_BACKGROUND_CONTENT_TYPES. */
const MAX_BACKGROUND_MB = 10
const BACKGROUND_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif'

/**
 * Matches MIN_REGISTRANT_SEARCH_LENGTH in Events::CertificatesController — below
 * it the server hands back the same opening list, so there is nothing to gain
 * from the round trip.
 */
const MIN_REGISTRANT_SEARCH_LENGTH = 2
/** The house debounce for a server-backed search box; see user_management. */
const REGISTRANT_SEARCH_DEBOUNCE_MS = 300
/**
 * Stable identity for "the search has not answered yet". A fresh `[]` per render
 * would invalidate the options memo on every canvas drag frame.
 */
const NO_REGISTRANTS: CertificatePreviewRegistrant[] = []

/**
 * Canvas zoom. An A4 landscape page is 1169 design pixels wide, so unlike the
 * 400px badge canvas this one does not fit a normal viewport at 1:1 — the
 * viewport picks the largest of these that fits whenever the page size changes,
 * and the user can override it.
 */
const CANVAS_ZOOM_OPTIONS = [0.25, 0.5, 0.75, 1] as const
const CANVAS_VIEWPORT_INSET = 56

const MIN_ELEMENT_SIZE_PX = 16

type InteractionState =
  | {
      mode: 'drag'
      elementId: string
      startMouseX: number
      startMouseY: number
      startX: number
      startY: number
    }
  | {
      mode: 'resize'
      elementId: string
      startMouseX: number
      startMouseY: number
      startWidth: number
      startHeight: number
    }

/**
 * Fields a properties-panel edit may set on the selected element. Distributes
 * over the CertificateElement union (dropping the `id`/`type` discriminants) so
 * each variant contributes its own keys with their real value types.
 */
type CertificateElementUpdate = CertificateElement extends infer Element
  ? Element extends CertificateElement
    ? Partial<Omit<Element, 'id' | 'type'>>
    : never
  : never

export interface CertificateDesignTabProps {
  eventSlug: string
  eventData?: CertificateEventData
  certificateTemplate?: CertificateTemplate
  /** The designer's bare-key merge vocabulary, from `#merge_tag_props`. */
  mergeTags: CertificateMergeTag[]
  /**
   * The opening slice of the picker — the most recent PREVIEW_REGISTRANT_LIMIT
   * registrants, so the page has a preview subject before anyone types. May be
   * empty. Everyone else is reached through the search box.
   */
  registrantsForPreview: CertificatePreviewRegistrant[]
  /**
   * How many registrants the event actually has. The picker needs it to admit
   * how much of the event `registrantsForPreview` is not showing; without it a
   * 414-attendee event silently looks like a ten-attendee one.
   */
  registrantCount?: number
}

/** One `<Select>` row for a registrant, labelled the way the picker shows them. */
function toRegistrantOption(registrant: CertificatePreviewRegistrant) {
  const name = registrant.full_name || registrant.email || `Registrant #${registrant.id}`

  return {
    label: `${name} (${registrant.email || 'no-email'})`,
    value: registrant.id,
  }
}

type AutoFitTextBoxProps = {
  text: string
  fontFamily: string
  fontWeight: string
  textAlign: CertificateTextAlign
  textTransform: CertificateTextTransform
  color: string
  maxFontSize: number
}

/**
 * Approximates the renderer's `overflow: :shrink_to_fit, min_font_size: 6`:
 * start at the chosen size and step down until the text fits its box. The floor
 * is the design-pixel equivalent of the renderer's 6pt so the two agree on when
 * shrinking stops.
 */
function AutoFitTextBox({
  text,
  fontFamily,
  fontWeight,
  textAlign,
  textTransform,
  color,
  maxFontSize,
}: AutoFitTextBoxProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let nextSize = Math.max(CERT_MIN_FONT_SIZE_PX, Math.round(maxFontSize))
    el.style.fontSize = `${nextSize}px`
    el.style.lineHeight = '1.15'

    while (
      nextSize > CERT_MIN_FONT_SIZE_PX &&
      (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)
    ) {
      nextSize -= 1
      el.style.fontSize = `${nextSize}px`
    }
  }, [text, fontFamily, fontWeight, textAlign, textTransform, color, maxFontSize])

  return (
    <div
      ref={ref}
      className="w-full h-full overflow-hidden whitespace-pre-wrap break-words"
      style={{ fontFamily, fontWeight, textAlign, textTransform, color }}
    >
      {text}
    </div>
  )
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!(error instanceof Error)) return fallback
  const candidate = error as Error & { data?: { errors?: string[] } }
  return candidate.data?.errors?.join(', ') || error.message || fallback
}

const elementTypeLabel = (element: CertificateElement, mergeTags: CertificateMergeTag[]) => {
  if (element.type === 'text') return element.text.trim() || 'Text'
  const tag = mergeTags.find((candidate) => candidate.key === element.merge_key)
  return `Merge: ${tag?.label || element.merge_key}`
}

/**
 * Exactly the shape `Events::CertificatesController#normalized_template_payload`
 * reads. Built from a preset-consistent copy so a fixed preset can never save a
 * page size that disagrees with its own name, and with `z_index` re-stamped from
 * the layer order so the renderer's sort is deterministic.
 */
const buildTemplatePayload = (template: CertificateTemplate) => {
  const consistent = ensureCertificatePresetConsistency(template)
  return {
    preset: consistent.preset,
    width_in: consistent.width_in,
    height_in: consistent.height_in,
    elements: withSequentialZIndex(consistent.elements),
  }
}

/** Serialised design, used only to tell a dirty canvas from a saved one. */
const templateSignature = (template: CertificateTemplate) =>
  JSON.stringify(buildTemplatePayload(template))

/**
 * The certificate designer: page setup and background artwork on the left, a
 * 100-DPI canvas of draggable text and merge-tag boxes on the right.
 *
 * Everything it writes is read straight back by `Certificates::PdfRenderer` —
 * see CERT_DESIGN_DPI and CERT_FONT_FAMILIES for the two constants that have to
 * agree with it.
 */
export default function CertificateDesignTab({
  eventSlug,
  eventData,
  certificateTemplate,
  mergeTags,
  registrantsForPreview,
  registrantCount = 0,
}: CertificateDesignTabProps) {
  const [template, setTemplate] = useState<CertificateTemplate>(() =>
    normalizeCertificateTemplate(certificateTemplate || createDefaultCertificateTemplate())
  )
  const [savedSignature, setSavedSignature] = useState(() =>
    templateSignature(normalizeCertificateTemplate(certificateTemplate))
  )
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null)
  const [interaction, setInteraction] = useState<InteractionState | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingBackground, setIsUploadingBackground] = useState(false)
  const [backgroundFileList, setBackgroundFileList] = useState<UploadFile[]>([])
  const [selectedMergeTagKey, setSelectedMergeTagKey] = useState<string>(
    () => mergeTags[0]?.key || 'full_name'
  )
  /**
   * The whole record, not just its id. A registrant found through the search box
   * is not in `registrantsForPreview`, so an id alone could neither label the
   * closed picker nor feed the canvas — the preview would silently snap back to
   * whoever happened to be first in the opening list.
   */
  const [selectedRegistrant, setSelectedRegistrant] = useState<CertificatePreviewRegistrant | null>(
    () => registrantsForPreview[0] ?? null
  )
  const [registrantSearch, setRegistrantSearch] = useState('')
  const [debouncedRegistrantSearch, setDebouncedRegistrantSearch] = useState('')
  const [zoom, setZoom] = useState<number>(1)
  const canvasViewportRef = useRef<HTMLDivElement | null>(null)

  const isDirty = useMemo(
    () => templateSignature(template) !== savedSignature,
    [template, savedSignature]
  )

  /**
   * Mirrors `isDirty` for the loader below, which has to consult it without
   * listing it as a dependency: depending on `isDirty` would re-run the loader
   * the instant the canvas went clean, which is precisely the wrong moment to
   * reload. Declared before the loader so React flushes this first and the
   * loader always reads the current render's value.
   */
  const isDirtyRef = useRef(isDirty)
  useEffect(() => {
    isDirtyRef.current = isDirty
  }, [isDirty])

  /**
   * The row this canvas was loaded from. `null` covers both "no template prop"
   * and the built-but-unsaved record a brand new event arrives with, which the
   * server sends as `id: null`.
   */
  const incomingTemplateId = certificateTemplate?.id ?? null
  const loadedTemplateIdRef = useRef(incomingTemplateId)

  /**
   * Loads a *different* template row onto the canvas. Deliberately not keyed on
   * the identity of the `certificateTemplate` prop.
   *
   * Both tabs of this page share one Inertia page object and the designer is
   * never unmounted while you are on it — `router.patch` defaults to
   * `preserveState: true`, and `AutomatedEmailForm` does not override it. Saving
   * the Email Content tab therefore redirects back here (on validation failure
   * as well as success), rebuilding the props with a brand new object every
   * time. Keyed on identity, this effect fired on each of those and overwrote
   * hand-positioned elements with the last saved design, clearing the "Unsaved
   * changes" hint in the same tick so the loss happened silently under a green
   * success toast.
   *
   * Two guards, because the id alone is not enough. The row is created lazily —
   * the first save or background upload mints it — so `null -> id` is a real
   * transition that can land while the canvas is dirty, and reloading on it
   * would lose exactly the work that just created the row.
   *
   * Switching to another event is not this effect's problem: that is a GET
   * visit, which runs with `preserveState: false`, and Inertia remounts the page
   * component on those — every useState here re-seeds from the new props.
   */
  useEffect(() => {
    if (incomingTemplateId === loadedTemplateIdRef.current) return

    // Claimed either way: the props describe this row from here on, whether or
    // not we are in a position to draw it.
    loadedTemplateIdRef.current = incomingTemplateId
    if (isDirtyRef.current) return

    const next = normalizeCertificateTemplate(
      certificateTemplate || createDefaultCertificateTemplate()
    )
    setTemplate(next)
    setSavedSignature(templateSignature(next))
    // `certificateTemplate` is intentionally absent: its identity changes on
    // every prop refresh, which is the bug this effect exists to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingTemplateId])

  const canvasSize = useMemo(
    () => certificateCanvasSizeInPixels(template, CERT_DESIGN_DPI),
    [template.width_in, template.height_in]
  )

  // Refit whenever the page size changes — including on first paint. A manual
  // zoom survives everything except a change of page size, which is the one
  // moment the previous choice is guaranteed to be wrong.
  //
  // Width-only on purpose: the viewport is height-capped and scrolls, so a tall
  // page is readable at a useful zoom rather than shrunk to fit a dimension
  // nobody is measuring. This reads clientWidth, which is only trustworthy
  // because the pane can now shrink — before min-w-0 the pane grew to whatever
  // the canvas asked for, so clientWidth reported the overflowing width back and
  // the fit check compared the canvas against itself.
  useEffect(() => {
    const viewport = canvasViewportRef.current
    if (!viewport) return

    const available = viewport.clientWidth - CANVAS_VIEWPORT_INSET
    const fitting = [...CANVAS_ZOOM_OPTIONS]
      .reverse()
      .find((option) => canvasSize.width * option <= available)

    setZoom(fitting ?? CANVAS_ZOOM_OPTIONS[0])
  }, [canvasSize.width])

  const elements = template.elements

  const selectedElement = useMemo(
    () => elements.find((element) => element.id === selectedElementId) || null,
    [elements, selectedElementId]
  )

  /** Great Vibes and any future one-face family: nothing to pick, so no picker. */
  const singleWeightFont = isSingleWeightCertificateFont(selectedElement?.font_family ?? '')

  useEffect(() => {
    if (!selectedElementId) return
    if (!elements.some((element) => element.id === selectedElementId)) {
      setSelectedElementId(null)
    }
  }, [elements, selectedElementId])

  /**
   * Keeps the selection honest across an Inertia prop refresh without throwing
   * it away: prefer the server's newer copy of whoever is selected, fall back to
   * the one already held (a search hit is not in the opening list), and only
   * default to the first registrant when nothing is selected at all — which is
   * the load-time behaviour this picker has always had.
   */
  useEffect(() => {
    setSelectedRegistrant(
      (current) =>
        (current && registrantsForPreview.find((registrant) => registrant.id === current.id)) ||
        current ||
        registrantsForPreview[0] ||
        null
    )
  }, [registrantsForPreview])

  const previewData = useMemo(
    () => buildCertificatePreviewData(selectedRegistrant, eventData),
    [selectedRegistrant, eventData]
  )

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedRegistrantSearch(registrantSearch.trim()),
      REGISTRANT_SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
  }, [registrantSearch])

  const isSearchingRegistrants = debouncedRegistrantSearch.length >= MIN_REGISTRANT_SEARCH_LENGTH

  /**
   * Server-side on purpose: the picker only ever holds a page of the event, so
   * filtering what it holds would search the ten names already on screen rather
   * than the four hundred behind them.
   */
  const registrantSearchQuery = useQuery({
    queryKey: keys.certificatePreviewRegistrants.search(eventSlug, debouncedRegistrantSearch),
    queryFn: () =>
      api.get<CertificateRegistrantSearchResponse>(
        `/events/${eventSlug}/certificates/registrants?q=${encodeURIComponent(
          debouncedRegistrantSearch
        )}`
      ),
    enabled: isSearchingRegistrants,
  })

  const setElements = useCallback((next: CertificateElement[]) => {
    setTemplate((prev) => ({ ...prev, elements: next }))
  }, [])

  const updateSelectedElement = (updates: CertificateElementUpdate) => {
    if (!selectedElementId) return
    setElements(
      elements.map((element) =>
        element.id === selectedElementId ? ({ ...element, ...updates } as CertificateElement) : element
      )
    )
  }

  const appendElement = (element: CertificateElement) => {
    setElements([...elements, element])
    setSelectedElementId(element.id)
  }

  const removeSelectedElement = () => {
    if (!selectedElementId) return
    setElements(elements.filter((element) => element.id !== selectedElementId))
    setSelectedElementId(null)
  }

  /** A wide, centred box near the top — the shape almost every certificate line wants. */
  const nextElementBox = () => {
    const width = Math.max(MIN_ELEMENT_SIZE_PX, Math.round(canvasSize.width * 0.7))
    const height = 80
    return {
      x: Math.round((canvasSize.width - width) / 2),
      y: clamp(80 + elements.length * 16, 0, Math.max(0, canvasSize.height - height)),
      width,
      height,
    }
  }

  const addTextElement = () => {
    appendElement({
      id: createCertificateElementId(),
      type: 'text',
      text: 'Certificate of Attendance',
      ...nextElementBox(),
      ...createDefaultCertificateTextStyle(),
    })
  }

  const addMergeTagElement = (mergeKey: string) => {
    appendElement({
      id: createCertificateElementId(),
      type: 'merge_tag',
      merge_key: mergeKey,
      ...nextElementBox(),
      ...createDefaultCertificateTextStyle(),
    })
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const payload = buildTemplatePayload(template)
      const response = await api.patch<CertificateTemplateResponse>(
        `/events/${eventSlug}/certificates/template`,
        { certificate_template: payload }
      )

      if (response.certificateTemplate) {
        const saved = normalizeCertificateTemplate(response.certificateTemplate)
        // On a brand new event this save is what mints the row. Recording the id
        // it came back with keeps the loader effect a no-op when Inertia next
        // rebuilds the props around that same row.
        loadedTemplateIdRef.current = saved.id ?? loadedTemplateIdRef.current
        setTemplate(saved)
        setSavedSignature(templateSignature(saved))
      } else {
        setSavedSignature(JSON.stringify(payload))
      }

      message.success(response.message || 'Certificate saved')
    } catch (error: unknown) {
      message.error(getErrorMessage(error, 'Failed to save certificate'))
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * The upload endpoint persists the template row if it was new, so its response
   * carries the server's copy of `elements` — which is the last *saved* design,
   * not what is on the canvas right now. Only the identity and the artwork are
   * taken from it; taking the whole record would throw away unsaved edits.
   */
  const applyBackgroundResponse = (response: CertificateTemplateResponse) => {
    const persistedId = response.certificateTemplate?.id
    // Same reason as in `handleSave`: an upload persists a new row too, so the
    // loader must not treat the id arriving on the next prop refresh as a
    // different template.
    if (persistedId != null) loadedTemplateIdRef.current = persistedId

    setTemplate((prev) => ({
      ...prev,
      id: persistedId ?? prev.id,
      background_image_url: response.certificateTemplate?.background_image_url ?? null,
    }))
  }

  const handleBackgroundChange = async (fileList: UploadFile[]) => {
    const file = fileList[0]?.originFileObj
    if (!file) {
      setBackgroundFileList(fileList)
      return
    }

    setBackgroundFileList(fileList)
    setIsUploadingBackground(true)
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post<CertificateTemplateResponse>(
        `/events/${eventSlug}/certificates/background`,
        formData
      )

      applyBackgroundResponse(response)
      message.success(response.message || 'Background updated')
    } catch (error: unknown) {
      message.error(getErrorMessage(error, 'Failed to upload background'))
    } finally {
      // Cleared either way: the canvas and the preview both read the server URL
      // from here on, so a lingering local file would only shadow it.
      setBackgroundFileList([])
      setIsUploadingBackground(false)
    }
  }

  const handleBackgroundRemove = async () => {
    if (!template.background_image_url) return

    setIsUploadingBackground(true)
    try {
      const response = await api.delete<CertificateTemplateResponse>(
        `/events/${eventSlug}/certificates/background`
      )

      applyBackgroundResponse(response)
      message.success(response.message || 'Background removed')
    } catch (error: unknown) {
      message.error(getErrorMessage(error, 'Failed to remove background'))
    } finally {
      setBackgroundFileList([])
      setIsUploadingBackground(false)
    }
  }

  /**
   * Opens the server-rendered PDF. Authoritative on purpose: this canvas draws
   * with webfonts and the PDF embeds the vendored TTFs, so only the server can
   * show what a registrant actually receives.
   */
  const handlePreviewPdf = () => {
    if (isDirty) {
      message.warning('Preview shows the last saved design — save to include your latest changes.')
    }

    const query = new URLSearchParams()
    if (selectedRegistrant) {
      query.set('registrant_id', String(selectedRegistrant.id))
    }
    const queryString = query.toString()
    const url = `/events/${eventSlug}/certificates/preview${queryString ? `?${queryString}` : ''}`

    // `noopener` is severed afterwards rather than asked for in the feature
    // string: window.open returns null whenever the feature string carries it,
    // which would make the blocked-popup branch below fire on every successful
    // preview.
    const opened = window.open(url, '_blank')
    if (opened) {
      opened.opener = null
    } else {
      message.error('Allow pop-ups for this site to preview the certificate.')
    }
  }

  const startDrag = (event: ReactMouseEvent, element: CertificateElement) => {
    event.preventDefault()
    event.stopPropagation()
    setSelectedElementId(element.id)
    setInteraction({
      mode: 'drag',
      elementId: element.id,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      startX: element.x,
      startY: element.y,
    })
  }

  const startResize = (event: ReactMouseEvent, element: CertificateElement) => {
    event.preventDefault()
    event.stopPropagation()
    setSelectedElementId(element.id)
    setInteraction({
      mode: 'resize',
      elementId: element.id,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      startWidth: element.width,
      startHeight: element.height,
    })
  }

  useEffect(() => {
    if (!interaction) return

    const onMouseMove = (event: MouseEvent) => {
      // Pointer travel is in screen pixels; the canvas is drawn at `zoom`, and
      // element geometry is stored in design pixels. Undo the scale or a zoomed
      // canvas would move elements by the wrong distance.
      const deltaX = (event.clientX - interaction.startMouseX) / zoom
      const deltaY = (event.clientY - interaction.startMouseY) / zoom

      setTemplate((prev) => {
        const next = [...prev.elements]
        const index = next.findIndex((item) => item.id === interaction.elementId)
        if (index < 0) return prev

        const current = next[index]
        if (interaction.mode === 'drag') {
          next[index] = {
            ...current,
            x: clamp(interaction.startX + deltaX, 0, canvasSize.width - current.width),
            y: clamp(interaction.startY + deltaY, 0, canvasSize.height - current.height),
          }
        } else {
          next[index] = {
            ...current,
            width: clamp(
              interaction.startWidth + deltaX,
              MIN_ELEMENT_SIZE_PX,
              canvasSize.width - current.x
            ),
            height: clamp(
              interaction.startHeight + deltaY,
              MIN_ELEMENT_SIZE_PX,
              canvasSize.height - current.y
            ),
          }
        }

        return { ...prev, elements: next }
      })
    }

    const onMouseUp = () => setInteraction(null)

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [interaction, zoom, canvasSize.width, canvasSize.height])

  const handlePresetChange = (preset: CertificatePreset) => {
    setTemplate((prev) => ensureCertificatePresetConsistency({ ...prev, preset }))
  }

  const renderElementPreview = (element: CertificateElement) => {
    const resolved = resolveCertificateElementText(element, previewData)
    const text =
      element.type === 'merge_tag' && !resolved ? `{{${element.merge_key}}}` : resolved

    return (
      <AutoFitTextBox
        text={text}
        fontFamily={certificateFontCss(element.font_family)}
        fontWeight={element.font_weight}
        textAlign={element.text_align}
        textTransform={element.text_transform}
        color={element.color}
        maxFontSize={element.font_size}
      />
    )
  }

  const mergeTagOptions = useMemo(
    () => mergeTags.map((tag) => ({ label: tag.label, value: tag.key })),
    [mergeTags]
  )

  /**
   * The event's real size. `registrantCount` is what the server counted;
   * falling back to the list length keeps the picker honest rather than
   * claiming "0 of 0" if the prop ever goes missing.
   */
  const totalRegistrants = Math.max(registrantCount, registrantsForPreview.length)

  /** What the dropdown is currently listing, and how many exist behind it. */
  const registrantResults = isSearchingRegistrants
    ? registrantSearchQuery.data?.results ?? NO_REGISTRANTS
    : registrantsForPreview
  const matchingRegistrantCount = isSearchingRegistrants
    ? registrantSearchQuery.data?.total ?? 0
    : totalRegistrants

  const registrantOptions = useMemo(() => {
    // The selected registrant is pinned in front of whatever the server just
    // returned. Without it, searching for somebody else drops the current
    // selection out of `options` and antd falls back to rendering the bare id —
    // the picker looks like it reset itself mid-search.
    const listed =
      selectedRegistrant &&
      !registrantResults.some((registrant) => registrant.id === selectedRegistrant.id)
        ? [selectedRegistrant, ...registrantResults]
        : registrantResults

    return listed.map(toRegistrantOption)
  }, [registrantResults, selectedRegistrant])

  const hasRegistrants = totalRegistrants > 0
  const isRegistrantSearchPending = registrantSearchQuery.isFetching

  /**
   * What the picker is not showing. `results` is capped server-side
   * (REGISTRANT_SEARCH_LIMIT) and so is the opening list
   * (PREVIEW_REGISTRANT_LIMIT), so an admin who cannot find someone has to be
   * told the list was cut rather than left to conclude they never registered.
   */
  const registrantCapNotice = useMemo(() => {
    if (!hasRegistrants || isRegistrantSearchPending) return null

    const shown = registrantResults.length
    if (shown >= matchingRegistrantCount) return null

    return isSearchingRegistrants
      ? `Showing ${shown} of ${matchingRegistrantCount} matches — refine your search.`
      : `Showing ${shown} of ${matchingRegistrantCount} registrants — type at least ${MIN_REGISTRANT_SEARCH_LENGTH} characters to search the rest.`
  }, [
    hasRegistrants,
    isRegistrantSearchPending,
    isSearchingRegistrants,
    matchingRegistrantCount,
    registrantResults.length,
  ])

  const backgroundUrl = template.background_image_url || null

  return (
    <div className="grid gap-4 xl:grid-cols-[420px_minmax(0,1fr)]">
      <div className="min-w-0 bg-white border border-gray-200 rounded-md p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold m-0">Certificate Setup</h3>
          <Button
            variant="primary"
            icon={<SaveOutlined />}
            loading={isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Page Size</label>
          <Select
            value={template.preset}
            options={CERT_PRESETS}
            onChange={(value) => handlePresetChange(value as CertificatePreset)}
          />
        </div>

        {template.preset === 'custom' && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Width (in)</label>
                <InputNumber
                  min={MIN_CERT_DIMENSION_IN}
                  max={MAX_CERT_DIMENSION_IN}
                  step={0.1}
                  value={template.width_in}
                  onChange={(value) =>
                    setTemplate((prev) => ({
                      ...prev,
                      width_in: typeof value === 'number' ? value : prev.width_in,
                    }))
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Height (in)</label>
                <InputNumber
                  min={MIN_CERT_DIMENSION_IN}
                  max={MAX_CERT_DIMENSION_IN}
                  step={0.1}
                  value={template.height_in}
                  onChange={(value) =>
                    setTemplate((prev) => ({
                      ...prev,
                      height_in: typeof value === 'number' ? value : prev.height_in,
                    }))
                  }
                  className="w-full"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 m-0">
              {MIN_CERT_DIMENSION_IN}–{MAX_CERT_DIMENSION_IN} inches per side. Canvas:{' '}
              {canvasSize.width} x {canvasSize.height} px.
            </p>
          </div>
        )}

        <div className="pt-2 border-t border-gray-200">
          <ImageUploadWithPreview
            label="Background Artwork"
            fileList={backgroundFileList}
            onChange={handleBackgroundChange}
            onRemove={handleBackgroundRemove}
            previewUrl={backgroundUrl}
            previewAlt="Certificate background"
            accept={BACKGROUND_ACCEPT}
            maxSizeMB={MAX_BACKGROUND_MB}
            disabled={isUploadingBackground}
            buttonText="Upload Background"
            helpText={`JPEG, PNG, WebP, GIF or HEIC up to ${MAX_BACKGROUND_MB}MB. Stretched to fill the whole page.`}
          />
        </div>

        <div className="pt-2 border-t border-gray-200 space-y-2">
          <h4 className="text-sm font-semibold m-0">Insert Component</h4>
          <Button block variant="secondary" icon={<PlusOutlined />} onClick={addTextElement}>
            Text
          </Button>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
            <Select
              className="min-w-0"
              value={selectedMergeTagKey}
              options={mergeTagOptions}
              onChange={(value) => setSelectedMergeTagKey(String(value))}
            />
            <Button variant="secondary" onClick={() => addMergeTagElement(selectedMergeTagKey)}>
              Add Tag
            </Button>
          </div>
        </div>

        <div className="pt-2 border-t border-gray-200 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold m-0">Layers</h4>
            <Tooltip title="Delete selected element">
              <Button
                variant="text"
                icon={<DeleteOutlined />}
                onClick={removeSelectedElement}
                disabled={!selectedElementId}
                danger
              >
                Delete
              </Button>
            </Tooltip>
          </div>
          <div className="max-h-36 overflow-y-auto border border-gray-200 rounded p-2 space-y-1">
            {elements.length === 0 && (
              <p className="text-xs text-gray-500 m-0">
                No elements yet — add a text box or a merge tag.
              </p>
            )}
            {elements.map((element, index) => (
              <button
                key={element.id}
                type="button"
                className={`w-full text-left px-2 py-1 rounded text-xs truncate ${
                  selectedElementId === element.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setSelectedElementId(element.id)}
              >
                {index + 1}. {elementTypeLabel(element, mergeTags)}
              </button>
            ))}
          </div>
          {elements.length > 1 && (
            <p className="text-xs text-gray-500 m-0">Later layers are drawn on top.</p>
          )}
        </div>

        {selectedElement && (
          <div className="pt-2 border-t border-gray-200 space-y-2">
            <h4 className="text-sm font-semibold m-0">Properties</h4>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="text-xs text-gray-600 block mb-1">X</label>
                <InputNumber
                  value={selectedElement.x}
                  min={0}
                  max={canvasSize.width}
                  onChange={(value) =>
                    updateSelectedElement({
                      x: typeof value === 'number' ? value : selectedElement.x,
                    })
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Y</label>
                <InputNumber
                  value={selectedElement.y}
                  min={0}
                  max={canvasSize.height}
                  onChange={(value) =>
                    updateSelectedElement({
                      y: typeof value === 'number' ? value : selectedElement.y,
                    })
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Width</label>
                <InputNumber
                  value={selectedElement.width}
                  min={MIN_ELEMENT_SIZE_PX}
                  max={canvasSize.width}
                  onChange={(value) =>
                    updateSelectedElement({
                      width: typeof value === 'number' ? value : selectedElement.width,
                    })
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Height</label>
                <InputNumber
                  value={selectedElement.height}
                  min={MIN_ELEMENT_SIZE_PX}
                  max={canvasSize.height}
                  onChange={(value) =>
                    updateSelectedElement({
                      height: typeof value === 'number' ? value : selectedElement.height,
                    })
                  }
                  className="w-full"
                />
              </div>
            </div>

            {selectedElement.type === 'text' ? (
              <div>
                <label className="text-xs text-gray-600 block mb-1">Text</label>
                <Input
                  value={selectedElement.text}
                  onChange={(event) => updateSelectedElement({ text: event.target.value })}
                />
              </div>
            ) : (
              <div>
                <label className="text-xs text-gray-600 block mb-1">Merge Tag</label>
                <Select
                  value={selectedElement.merge_key}
                  options={mergeTagOptions}
                  onChange={(value) => updateSelectedElement({ merge_key: String(value) })}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Font</label>
                <Select
                  value={selectedElement.font_family}
                  options={CERT_FONT_FAMILIES.map((font) => ({ label: font, value: font }))}
                  onChange={(value) => {
                    // The weight travels with the family. Switching to a family
                    // that ships one face has to drop a stored 700, or the box
                    // keeps a weight the PDF will not draw.
                    const font_family = String(value)
                    updateSelectedElement({
                      font_family,
                      font_weight: coerceCertificateFontWeight(
                        font_family,
                        selectedElement.font_weight
                      ),
                    })
                  }}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Size</label>
                <InputNumber
                  value={selectedElement.font_size}
                  min={CERT_MIN_FONT_SIZE_PX}
                  max={CERT_MAX_FONT_SIZE_PX}
                  onChange={(value) =>
                    updateSelectedElement({
                      font_size: typeof value === 'number' ? value : selectedElement.font_size,
                    })
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Weight</label>
                {/* Single-weight families get a disabled control with the reason,
                    rather than a Bold option that would render as a fake browser
                    bold on the canvas and a regular face in the PDF. Antd needs
                    the wrapper span: a disabled Select swallows the hover the
                    Tooltip listens for. */}
                <Tooltip
                  title={
                    singleWeightFont
                      ? `${selectedElement.font_family} is published in one weight only, so bold cannot be embedded in the PDF.`
                      : ''
                  }
                >
                  <span className="block">
                    <Select
                      value={selectedElement.font_weight}
                      options={certificateFontWeightOptions(selectedElement.font_family)}
                      disabled={singleWeightFont}
                      onChange={(value) => updateSelectedElement({ font_weight: String(value) })}
                    />
                  </span>
                </Tooltip>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Align</label>
                <Select
                  value={selectedElement.text_align}
                  options={[
                    { label: 'Left', value: 'left' },
                    { label: 'Center', value: 'center' },
                    { label: 'Right', value: 'right' },
                  ]}
                  onChange={(value) =>
                    updateSelectedElement({ text_align: value as CertificateTextAlign })
                  }
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Case</label>
                <Select
                  value={selectedElement.text_transform}
                  options={[
                    { label: 'Normal', value: 'none' },
                    { label: 'ALL CAPS', value: 'uppercase' },
                    { label: 'Capitalize', value: 'capitalize' },
                  ]}
                  onChange={(value) =>
                    updateSelectedElement({ text_transform: value as CertificateTextTransform })
                  }
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Colour</label>
                <input
                  type="color"
                  value={selectedElement.color || '#111827'}
                  onChange={(event) => updateSelectedElement({ color: event.target.value })}
                  className="w-full h-8 border border-gray-200 rounded"
                  aria-label="Text colour"
                />
              </div>
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-gray-200 space-y-2">
          <h4 className="text-sm font-semibold m-0">Preview PDF</h4>
          <div>
            <label className="text-xs text-gray-600 block mb-1" id="certificate-registrant-label">
              Registrant
            </label>
            <Select
              aria-labelledby="certificate-registrant-label"
              disabled={!hasRegistrants}
              value={selectedRegistrant?.id}
              options={registrantOptions}
              placeholder={hasRegistrants ? 'Search by name or email' : 'No registrant available'}
              showSearch
              // Server-side: the options below are a page of the event, not the
              // event, so filtering them locally would hide the very people the
              // search just went and found.
              filterOption={false}
              onSearch={setRegistrantSearch}
              // antd clears its own search box on select and on close; mirroring
              // that here keeps the dropdown from reopening onto stale results.
              onOpenChange={(open) => {
                if (!open) setRegistrantSearch('')
              }}
              loading={isRegistrantSearchPending}
              notFoundContent={
                isRegistrantSearchPending ? (
                  <div className="py-2 text-center">
                    <Spin size="small" />
                  </div>
                ) : (
                  <p className="text-xs text-black/45 py-2 text-center m-0">
                    {isSearchingRegistrants
                      ? 'No registrant matches that name or email.'
                      : `Type at least ${MIN_REGISTRANT_SEARCH_LENGTH} characters to search.`}
                  </p>
                )
              }
              popupRender={(menu) => (
                <>
                  {menu}
                  {registrantCapNotice && (
                    <p className="text-xs text-black/45 px-3 py-2 m-0 border-t border-gray-200">
                      {registrantCapNotice}
                    </p>
                  )}
                </>
              )}
              onChange={(value) => {
                const chosen = registrantResults.find((registrant) => registrant.id === value)
                // A re-pick of the pinned selection is not in `registrantResults`;
                // keeping the current record beats blanking the canvas.
                setSelectedRegistrant((current) => chosen ?? current)
                setRegistrantSearch('')
              }}
            />
            {!hasRegistrants && (
              <p className="text-xs text-amber-600 mt-1 mb-0">
                No registrants yet — the preview uses sample attendee data.
              </p>
            )}
          </div>
          <Button
            block
            variant="primary"
            icon={<FilePdfOutlined />}
            onClick={handlePreviewPdf}
          >
            Preview PDF
          </Button>
          <p className="text-xs text-gray-500 m-0">
            Rendered on the server with the embedded certificate fonts. This is what registrants
            receive; the canvas is only an approximation.
          </p>
          {isDirty && (
            <p className="text-xs text-amber-600 m-0">
              Unsaved changes — the preview shows the last saved design.
            </p>
          )}
        </div>
      </div>

      {/* min-w-0 is load-bearing, not defensive. A grid item's automatic minimum
          size is its min-content width, and this pane's min-content is the zoomed
          canvas below — 1169px for A4 landscape at 100%, 2000px for a 20in custom
          page. Without it the `1fr` track (itself minmax(auto, 1fr)) refuses to
          shrink past that, the grid grows wider than the viewport, and the body
          scrolls horizontally with the 420px properties panel pushed off-screen —
          while the overflow-auto container below never scrolls, because it was
          handed all the width it asked for. */}
      <div className="min-w-0 bg-white border border-gray-200 rounded-md p-4">
        <div className="flex items-center justify-between mb-3 gap-3">
          <h3 className="text-base font-semibold m-0">Certificate Canvas</h3>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600" htmlFor="certificate-canvas-zoom">
              Zoom
            </label>
            <Select
              id="certificate-canvas-zoom"
              block={false}
              value={zoom}
              style={{ width: 96 }}
              options={CANVAS_ZOOM_OPTIONS.map((option) => ({
                label: `${Math.round(option * 100)}%`,
                value: option,
              }))}
              onChange={(value) => setZoom(Number(value))}
            />
          </div>
        </div>

        {/* Bounded on both axes so an oversized design scrolls in here rather than
            stretching the page: a 20in custom canvas is 2000 design pixels each
            way, taller than any viewport at 100%. */}
        <div
          ref={canvasViewportRef}
          className="max-h-[70vh] overflow-auto bg-gray-100 rounded-md p-6 border border-gray-200"
        >
          {/* Sized to the scaled canvas so the scroll area tracks the zoom, while
              the canvas itself keeps its 1:1 design-pixel coordinate system. A
              scaled element's layout box does not grow, so without this wrapper
              the viewport would think the content fits and never show a scrollbar. */}
          <div
            style={{
              width: canvasSize.width * zoom,
              height: canvasSize.height * zoom,
            }}
          >
            <div
              className="relative bg-white shadow select-none"
              style={{
                width: canvasSize.width,
                height: canvasSize.height,
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
                backgroundImage: backgroundUrl ? `url(${JSON.stringify(backgroundUrl)})` : undefined,
                // Matches Certificates::PdfRenderer#draw_background, which stretches
                // the artwork across the whole page rather than preserving its ratio.
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
              }}
              onMouseDown={() => setSelectedElementId(null)}
            >
              {elements.map((element) => (
                <div
                  key={element.id}
                  className={`absolute cursor-move ${
                    selectedElementId === element.id
                      ? 'ring-2 ring-blue-500'
                      : 'ring-1 ring-transparent hover:ring-blue-200'
                  }`}
                  style={{
                    left: element.x,
                    top: element.y,
                    width: element.width,
                    height: element.height,
                  }}
                  onMouseDown={(event) => startDrag(event, element)}
                  onClick={(event) => {
                    event.stopPropagation()
                    setSelectedElementId(element.id)
                  }}
                >
                  {renderElementPreview(element)}
                  {selectedElementId === element.id && (
                    <button
                      type="button"
                      className="absolute -right-2 -bottom-2 w-4 h-4 rounded-sm bg-blue-600 border border-white cursor-se-resize"
                      onMouseDown={(event) => startResize(event, element)}
                      aria-label="Resize"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
